import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bars3Icon, 
  XMarkIcon,
  HomeIcon,
  UserIcon,
  DocumentTextIcon,
  BeakerIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  CalculatorIcon,
  FireIcon,
  PaperAirplaneIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();

  const navigation = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'About', href: '/about', icon: UserIcon },
    { name: 'Resume', href: '/resume', icon: DocumentTextIcon },
    { name: 'Projects', href: '/projects', icon: BeakerIcon },
    { name: 'Finance', href: '/finance', icon: ChartBarIcon },
  ];

  const toolsDropdownItems = [
    { name: 'Engineering', href: '/tools', icon: CalculatorIcon },
    { name: 'Fire Quiz', href: '/quiz', icon: FireIcon },
    { name: 'Study', href: '/study', icon: BookOpenIcon },
    { name: 'SR20 Quiz', href: '/pilot', icon: PaperAirplaneIcon },
    { name: 'SR20', href: '/sr20', icon: PaperAirplaneIcon },
  ];

  const isActive = (path) => location.pathname === path;
  const isToolsActive = () => location.pathname === '/tools' || location.pathname === '/quiz' || location.pathname === '/study' || location.pathname === '/pilot' || location.pathname === '/sr20';

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 w-full z-50 glass-effect border-b border-white/10"
    >
      <div className="container-custom">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 text-white font-bold text-xl"
          >
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
              className="w-8 h-8 bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg flex items-center justify-center"
            >
              M
            </motion.div>
            <span className="font-display">Maycon Meier</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`nav-link px-4 py-2 rounded-lg flex items-center space-x-2 ${
                    isActive(item.href) 
                      ? 'bg-white/20 text-white' 
                      : 'hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
            
            {/* Tools Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                className={`nav-link px-4 py-2 rounded-lg flex items-center space-x-2 ${
                  isToolsActive() 
                    ? 'bg-white/20 text-white' 
                    : 'hover:bg-white/10'
                }`}
              >
                <Cog6ToothIcon className="w-4 h-4" />
                <span>Tools</span>
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${isToolsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              {isToolsDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full mt-2 w-48 bg-white/10 backdrop-blur-lg rounded-lg border border-white/20 shadow-lg"
                  onMouseLeave={() => setIsToolsDropdownOpen(false)}
                >
                  {toolsDropdownItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsToolsDropdownOpen(false)}
                        className={`flex items-center space-x-2 px-4 py-3 text-white hover:bg-white/10 first:rounded-t-lg last:rounded-b-lg ${
                          isActive(item.href) ? 'bg-white/20' : ''
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </motion.div>
              )}
            </div>
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Auth Actions */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link
                  to="/dashboard"
                  className="nav-link px-4 py-2 rounded-lg hover:bg-white/10"
                >
                  Dashboard
                </Link>
                <button
                  onClick={logout}
                  className="flex items-center space-x-2 text-white/90 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="btn-primary px-6 py-2 text-sm"
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            {isOpen ? (
              <XMarkIcon className="w-6 h-6 text-white" />
            ) : (
              <Bars3Icon className="w-6 h-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden py-4 border-t border-white/10"
          >
            <div className="flex flex-col space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`nav-link px-4 py-3 rounded-lg flex items-center space-x-3 ${
                      isActive(item.href) 
                        ? 'bg-white/20 text-white' 
                        : 'hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              
              {/* Mobile Tools Dropdown */}
              <div>
                <button
                  onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                  className={`w-full nav-link px-4 py-3 rounded-lg flex items-center space-x-3 ${
                    isToolsActive() 
                      ? 'bg-white/20 text-white' 
                      : 'hover:bg-white/10'
                  }`}
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                  <span>Tools</span>
                  <ChevronDownIcon className={`w-5 h-5 ml-auto transition-transform ${isToolsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Mobile Dropdown Items */}
                {isToolsDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="ml-4 mt-2 space-y-2"
                  >
                    {toolsDropdownItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => {
                            setIsOpen(false);
                            setIsToolsDropdownOpen(false);
                          }}
                          className={`nav-link px-4 py-3 rounded-lg flex items-center space-x-3 ${
                            isActive(item.href) 
                              ? 'bg-white/20 text-white' 
                              : 'hover:bg-white/10'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </motion.div>
                )}
              </div>
              
              <div className="pt-4 border-t border-white/10">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className="nav-link px-4 py-3 rounded-lg flex items-center space-x-3 hover:bg-white/10"
                    >
                      <ChartBarIcon className="w-5 h-5" />
                      <span>Dashboard</span>
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setIsOpen(false);
                      }}
                      className="w-full text-left nav-link px-4 py-3 rounded-lg flex items-center space-x-3 hover:bg-white/10"
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="block w-full btn-primary text-center py-3"
                  >
                    Login
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;
